import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import camelcaseKeys from 'camelcase-keys';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Avatar, IconButton, Button } from '@mui/material';
import {
  ChevronRightRounded,
  FiberManualRecordRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  PersonRounded,
  PublicRounded,
  RunningWithErrorsRounded,
  TimelapseRounded,
} from '@mui/icons-material';
import {
  getCategoryLabel,
  getVoiceStatusLabel,
} from '@src/services/voiceCloning';
import {
  VOICE_STATUS,
  VOICE_STATUS_COLORS,
  SUB_STATUS,
  MAXIMUM_NUMBER_OF_ACTIVE_VOICES,
  REASON_VOICE_STATUS,
  VOICE_NOT_USED,
  VOICE_COMPLETED,
} from '@src/constants/voiceCloning';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import actions from '@src/redux/actions';
import route from '@src/constants/route';
import { StyledVoicesInformation, StyledChip } from './index.style';
import ViewReason from './ViewReason';
import NewVoiceDetailInfo from './NewVoiceDetailInfo';
import WarningDialog from './WarningDialog';

const VoiceInformation = ({
  id,
  avatar,
  name,
  category = {},
  status,
  province = {},
  gender,
  subStatus,
  note,
  voiceInfo,
  checkUserCanUseVoiceCloning,
  setOpenVoiceDetail,
  setReasonContent,
  setOpenViewReason,
  setViewReasonId,
  setIsShowContact,
  onContinueRecord,
  onShowUpgradePackage,
  handleDeleteVoice,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const renderVoiceStatus = (voiceStatus) => {
    const color = VOICE_STATUS_COLORS[voiceStatus];
    const label = getVoiceStatusLabel(voiceStatus);
    if (!color || !label) return '';
    return <StyledChip color={color}>{t(label)}</StyledChip>;
  };

  const renderSubStatus = () => {
    if (subStatus === SUB_STATUS.PRIVATE && note) {
      return (
        <RunningWithErrorsRounded className="name icon-size-20 error-color" />
      );
    }
    if (subStatus === SUB_STATUS.PRIVATE) {
      return <PersonRounded className="name icon-size-20" />;
    }
    if (subStatus === SUB_STATUS.PUBLIC) {
      return <PublicRounded className="name icon-size-20" />;
    }
    if (subStatus === SUB_STATUS.WAITING_FOR_PUBLIC) {
      return <TimelapseRounded className="name icon-size-20" />;
    }
    return <></>;
  };

  const onClickRedirectVoice = () => {
    if (!checkUserCanUseVoiceCloning) {
      onShowUpgradePackage();
      return;
    }
    dispatch(actions.voiceVoiceCloning.addVoice(voiceInfo));
    setOpenVoiceDetail(true);
  };

  const onClickShowReason = (event) => {
    event.stopPropagation();
    setReasonContent(note);
    setOpenViewReason(true);
    setViewReasonId(id);
    setIsShowContact(VOICE_NOT_USED.includes(status));
  };

  const continueRecording = (e) => {
    e.stopPropagation();
    onContinueRecord(id);
  };

  const onClickDeleteVoice = (e) => {
    e.stopPropagation();
    handleDeleteVoice(voiceInfo);
  };

  const onClickUseVoice = () => {
    if (!checkUserCanUseVoiceCloning) {
      onShowUpgradePackage();
      return;
    }
    dispatch(actions.synthesisRequest.updateVoice(voiceInfo));
    history.push(route.TTS);
  };

  const voiceInfoButtonAction = () => {
    const isVoiceDeletable = [
      VOICE_STATUS.RECORDING,
      VOICE_STATUS.REJECTED,
      VOICE_STATUS.READY,
      VOICE_STATUS.USING,
      VOICE_STATUS.FAILED,
      VOICE_STATUS.SUSPENDED,
    ].includes(status);

    const shouldShowDeleteVoiceOption =
      !checkUserCanUseVoiceCloning && isVoiceDeletable;

    if (shouldShowDeleteVoiceOption) {
      return (
        <Box className="view-reason" onClick={onClickDeleteVoice}>
          <Box className="view-reason-text">{t('deleteVoice')}</Box>
        </Box>
      );
    }
    if (REASON_VOICE_STATUS.includes(status))
      return (
        <Box className="view-reason" onClick={onClickShowReason}>
          <Box className="view-reason-text">{t('viewReason')}</Box>
        </Box>
      );

    if (status === VOICE_STATUS.USING && !isMobile)
      return (
        <Button
          variant="contained"
          className="use-now-button"
          onClick={onClickUseVoice}
        >
          <Typography className="use-now-text">{t('useNow')}</Typography>
        </Button>
      );

    if ([VOICE_STATUS.RECORDING, VOICE_STATUS.REJECTED].includes(status))
      return (
        <Box className="view-reason" onClick={continueRecording}>
          <Box className="view-reason-text">{t('continueRecording')}</Box>
        </Box>
      );

    return <></>;
  };

  return (
    <Box
      className="voice"
      onClick={onClickRedirectVoice}
      id={`voice-${voiceInfo?.id}`}
    >
      <Avatar src={avatar} alt="avatar" className="avatar" />
      <Box className="voice-info">
        <Box className="name-info">
          <Typography className="name">{name}</Typography>
          {province?.id && (
            <>
              <FiberManualRecordRounded className="name icon-size-6" />
              <Typography className="name">{province?.name}</Typography>
            </>
          )}
          {subStatus &&
            ![SUB_STATUS.PROCESSING, SUB_STATUS.ERROR].includes(subStatus) && (
              <>
                <FiberManualRecordRounded className="name icon-size-6" />
                {renderSubStatus()}
              </>
            )}
        </Box>
        <Box className="category-info">
          <Typography>{getCategoryLabel(category?.name)}</Typography>
          <FiberManualRecordRounded className="name icon-size-6" />
          <Typography>{t(gender)}</Typography>
        </Box>
        <Box className="status">{renderVoiceStatus(status)}</Box>
      </Box>
      <IconButton className="redirect-btn">
        <ChevronRightRounded fontSize="large" />
      </IconButton>
      {voiceInfoButtonAction()}
    </Box>
  );
};

const VoicesInformation = ({
  boxVoicesName,
  closeVoices,
  checkUserCanUseVoiceCloning,
  onCloseVoices,
  isVoiceStatusCompleted,
  voices,
  onContinueRecord,
  onDeleteVoice,
  handleChangeVoiceData,
  handleClickGenerateVoice,
  onShowUpgradePackage,
  onShowPopupLimitNumberVoiceCloning,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { voiceId } = camelcaseKeys(queryString.parse(location.search) || {});

  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);
  const useLimitVoiceCloning = getFeatureValue(
    FEATURE_KEYS.LIMIT_CREATE_VOICE_CLONING,
    { userId: user?.id },
  );

  const [openViewReason, setOpenViewReason] = useState(false);
  const [isShowContact, setIsShowContact] = useState(false);
  const [reasonContent, setReasonContent] = useState('');
  const [viewReasonId, setViewReasonId] = useState('');
  const [openVoiceDetail, setOpenVoiceDetail] = useState(false);

  // Warning dialog
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [warningTitle, setWarningTitle] = useState('');
  const [warningContent, setWarningContent] = useState('');
  const [warningTextConfirm, setWarningTextConfirm] = useState('');
  const [warningTextConfirmValue, setWarningTextConfirmValue] = useState('');
  const [isShowWarningContent, setIsShowWarningContent] = useState(true);

  const numberOfActiveVoices = voices.filter(
    (voice) => voice?.status === VOICE_STATUS.USING,
  ).length;

  const handleCloseWarningDialog = () => setOpenWarningDialog(false);

  const handleCloseViewReason = () => setOpenViewReason(false);

  const handleRecordAgain = () => {
    onContinueRecord(viewReasonId);
    setOpenViewReason(false);
  };

  const removeUrlParams = (name) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(name);
    history.push(url.pathname + url.search);
  };

  const scrollToVoice = (id) => {
    const element = document.getElementById(`voice-${id}`);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDeleteVoice = async (voice) => {
    // onClose();
    setWarningTitle(t('confirmDeleteVoice'));
    setWarningTextConfirm('confirmDeleteVoiceText');
    setWarningTextConfirmValue(voice);
    setIsShowWarningContent(false);
    setOpenWarningDialog(true);
  };

  useEffect(() => {
    if (voices.length > 0 && voiceId) {
      const voice = voices.find((v) => v.id === voiceId);
      if (voice) {
        dispatch(actions.voiceVoiceCloning.addVoice(voice));
        if (REASON_VOICE_STATUS.includes(voice.status)) {
          setReasonContent(voice.note);
          setOpenViewReason(true);
          setViewReasonId(voiceId);
          setIsShowContact(VOICE_NOT_USED.includes(voice.status));
          removeUrlParams('voiceId');
          scrollToVoice(voiceId);
          return;
        }

        if (VOICE_COMPLETED.includes(voice.status)) {
          setOpenVoiceDetail(true);
          removeUrlParams('voiceId');
          scrollToVoice(voiceId);
          return;
        }

        removeUrlParams('voiceId');
      }
    }
  }, [voices]);

  return (
    <StyledVoicesInformation>
      <Box className="box-title-icon-btn">
        <Box className="box-title-icon-btn">
          <Typography className="title">{boxVoicesName}</Typography>
          <IconButton className="arrow-icon-btn" onClick={onCloseVoices}>
            {closeVoices ? (
              <KeyboardArrowDownRounded />
            ) : (
              <KeyboardArrowUpRounded />
            )}
          </IconButton>
        </Box>
        {!useLimitVoiceCloning && isVoiceStatusCompleted && (
          <Typography className="sub-title">
            {t('voiceActivated')}{' '}
            <span className="span-sub-title">
              {`${numberOfActiveVoices}/${MAXIMUM_NUMBER_OF_ACTIVE_VOICES}`}
            </span>
          </Typography>
        )}
      </Box>
      {!closeVoices && (
        <Box className="voices">
          {voices.map((item) => (
            <VoiceInformation
              key={item.id}
              id={item.id}
              avatar={item.avatar}
              name={item.name}
              locale={item.locale}
              category={item?.category}
              gender={item.gender}
              status={item.status}
              province={item?.province}
              subStatus={item?.subStatus}
              note={item?.note}
              voiceInfo={item}
              checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
              setOpenVoiceDetail={setOpenVoiceDetail}
              setReasonContent={setReasonContent}
              setOpenViewReason={setOpenViewReason}
              setViewReasonId={setViewReasonId}
              setIsShowContact={setIsShowContact}
              onContinueRecord={onContinueRecord}
              onShowUpgradePackage={onShowUpgradePackage}
              handleDeleteVoice={handleDeleteVoice}
            />
          ))}
        </Box>
      )}
      <ViewReason
        viewReasonId={viewReasonId}
        open={openViewReason}
        note={reasonContent}
        onClose={handleCloseViewReason}
        handleRecordAgain={handleRecordAgain}
        isShowContact={isShowContact}
        handleClickGenerateVoice={handleClickGenerateVoice}
      />
      {openVoiceDetail && (
        <NewVoiceDetailInfo
          open={openVoiceDetail}
          onClose={() => setOpenVoiceDetail(false)}
          numberOfActiveVoices={numberOfActiveVoices}
          handleChangeVoiceData={handleChangeVoiceData}
          setWarningTitle={setWarningTitle}
          setWarningContent={setWarningContent}
          setWarningTextConfirm={setWarningTextConfirm}
          setOpenWarningDialog={setOpenWarningDialog}
          setWarningTextConfirmValue={setWarningTextConfirmValue}
          setIsShowWarningContent={setIsShowWarningContent}
          onShowPopupLimitNumberVoiceCloning={
            onShowPopupLimitNumberVoiceCloning
          }
        />
      )}
      <WarningDialog
        open={openWarningDialog}
        title={warningTitle}
        warningContent={warningContent}
        warningTextConfirm={warningTextConfirm}
        warningTextConfirmValue={warningTextConfirmValue}
        onClose={handleCloseWarningDialog}
        handleChangeVoiceData={handleChangeVoiceData}
        onDeleteVoice={onDeleteVoice}
        showWarningContent={isShowWarningContent}
      />
    </StyledVoicesInformation>
  );
};

export default VoicesInformation;
